export const translations = {
  en: {
    header: {
      brand: 'Digital Sidekick',
      tagline: 'Expert Guidance. Real Conversations.',
      cta: 'Open the App',
    },
    hero: {
      badge: 'app.digital-sidekicks.com',
      eyebrow: 'Portal AI Sidekick',
      title: 'Meet Your Digital Sidekick',
      subtitle: 'Expert guidance. Real conversations.',
      description:
        'An AI platform that gives you instant access to specialized experts through natural, human conversations. Not generic AI. Not passive tools. Real preparation for real life.',
      note: 'Available 24/7, private by design, hyper-personalized, and radically affordable — like a personal advisory board in your pocket.',
      cta: 'Get Started',
      secondaryCta: 'See How It Works',
    },
    problem: {
      label: 'The Problem',
      title: 'Life is full of moments where you can’t afford to get it wrong',
      situations: [
        'Job interviews',
        'Important dates',
        'Career decisions',
        'Difficult conversations',
        'Personal struggles',
        'Social situations',
      ],
      description:
        'Help is often expensive, hard to access, and booked weeks in advance. So people improvise — which leads to anxiety, missed opportunities, and regret.',
    },
    solution: {
      label: 'The Solution',
      title: 'Meet Your Digital Sidekick',
      subtitle:
        'An AI platform that gives users instant access to specialized experts through natural, human conversations.',
      highlight: 'Not generic AI. Not passive tools. Real preparation for real life.',
      description:
        'Available 24/7, private by design, hyper-personalized, and radically affordable — like a personal advisory board in your pocket.',
    },
    team: {
      title: 'Your Personal AI Team',
      subtitle: 'A specialist for every moment that matters',
      members: [
        {
          icon: '🎯',
          title: 'Interview Coach',
          description: 'Practice until confidence replaces nerves.',
        },
        {
          icon: '💬',
          title: 'Your AI Friend',
          description: 'Talk about anything without judgment — always there when you need it.',
        },
        {
          icon: '🧘',
          title: 'Wellbeing Companion',
          description: 'A safe place to reflect and strengthen your mental resilience.',
        },
        {
          icon: '♟️',
          title: 'Life Strategist',
          description: 'Take back control, navigate complexity, and design the future you want.',
        },
        {
          icon: '🗣️',
          title: 'Language Tutor',
          description: 'Speak naturally, do tasks together, and understand the deeper logic of language.',
        },
        {
          icon: '👨‍🍳',
          title: 'AI Chef',
          description: 'Cook smarter using the ingredients you already have.',
        },
        {
          icon: '🛋️',
          title: 'Interior Designer',
          description: 'Create a professionally styled home without professional prices.',
        },
        {
          icon: '✨',
          title: 'Lifestyle Coach',
          description: 'Turn intimidating moments into confident ones — from interviews to first dates.',
        },
      ],
    },
    lifestyle: {
      label: 'Lifestyle Coach',
      title: 'Confidence Can Be Trained',
      description:
        'Interviews, high-stakes tasks at work, business presentations and first dates can be intimidating. Fortunately, confidence is something you can practice. Rehearse conversations with the ‘date of your life’ in a completely safe environment.',
      points: [
        'Learn what to say — and what not to say',
        'Improve your flirting and communication',
        'Get smart, tailored advice',
        'Prepare for interviews, business presentations and first dates',
        'Handle awkward moments with ease',
        'Turn awkwardness into solutions',
      ],
    },
    day: {
      title: 'From Product to Daily Companion',
      subtitle: 'Digital Sidekick becomes part of people’s lives',
      steps: [
        {
          icon: '🌅',
          time: 'Morning',
          title: 'Plan your day with the strategist',
          description: 'Set your priorities and start the day with a clear plan.',
        },
        {
          icon: '📚',
          time: 'Afternoon',
          title: 'Practice a language',
          description: 'Speak naturally and build skills in real conversations.',
        },
        {
          icon: '🍳',
          time: 'Evening',
          title: 'Cook with the AI Chef',
          description: 'Turn what’s in your fridge into a great meal.',
        },
        {
          icon: '💘',
          time: 'Before a date',
          title: 'Rehearse with your Lifestyle Coach',
          description: 'Walk in confident and ready for the conversation.',
        },
      ],
    },
    benefits: {
      title: 'Key Benefits for Users',
      subtitle: 'Digital Sidekick becomes part of people’s lives',
      items: [
        {
          icon: '🚀',
          title: 'Confidence on Demand',
          description: 'Preparation removes fear.',
        },
        {
          icon: '⏰',
          title: 'Always Available',
          description: 'No scheduling or friction.',
        },
        {
          icon: '🎛️',
          title: 'Take Control of Your Life',
          description: 'Stop reacting and start designing.',
        },
        {
          icon: '💰',
          title: 'Expert Help Without Expert Prices',
          description: 'Save thousands every year.',
        },
        {
          icon: '🔒',
          title: 'A Private Space to Improve',
          description: 'Learn safely and faster.',
        },
      ],
      summary:
        'Digital Sidekick delivers progress without fear. Users feel more confident, less alone, better prepared and more capable — from everyday dinners to breakthrough dates and career-defining moments.',
    },
    why: {
      wins: {
        label: 'Why It Wins',
        title: 'A Platform of Personal Transformation',
        lead: 'Others deliver answers — we build better people.',
        description:
          'Our advantages include persona-based experiences, emotional intelligence, scenario-based practice, habit-forming use cases and very high life relevance.',
        highlight: 'We are defining the category of Personal AI Companions.',
      },
      now: {
        label: 'Why Now',
        title: 'The Timing Has Never Been Better',
        description:
          'AI is trusted, self-improvement is thriving, and consumers are building relationships with technology. The next dominant platforms will guide users — not just inform them.',
        highlight:
          'The question is no longer whether people will rely on AI, but which AI they’ll trust with their lives.',
      },
    },
    business: {
      model: {
        label: 'Business Model',
        title: 'Subscription & Expansion',
        cards: [
          {
            title: 'Subscription',
            description:
              'Premium includes unlimited access, advanced experts and engaging personas such as the Lifestyle Coach, strategist and tutor.',
          },
          {
            title: 'Expansion',
            description:
              'Future expansion includes relationship mastery packs, career acceleration packs, elite strategist tiers and confidence coaching.',
          },
        ],
        note: 'Built for strong retention and lifetime value.',
      },
      vision: {
        label: 'Vision',
        title: 'A Sidekick for Everyone',
        description:
          'Imagine a world where everyone has a coach, strategist, tutor, chef, designer, wellness and dating advisor — all in their pocket.',
        highlight: 'Not elite support — standard human infrastructure.',
        note: 'Our ambition is to become the default everyday AI companion.',
      },
    },
    service: {
      program: {
        label: 'Service Program',
        title: 'Where Human Expertise Meets Artificial Intelligence',
        items: [
          {
            title: 'Expert Integration',
            description:
              'We capture the nuance and empathy of human experts so every interaction is authentic and supportive.',
          },
          {
            title: 'Technical Synergy',
            description:
              'This expertise is seamlessly integrated with the advanced AI capabilities of our dedicated technical team.',
          },
          {
            title: 'Quality Assurance',
            description:
              'Every persona undergoes rigorous development to ensure the guidance is accurate, ethical and growth-oriented.',
          },
        ],
      },
      future: {
        label: 'The Future of Digital Personalities',
        title: 'Shaping the Future of Human-AI Interaction',
        cards: [
          {
            title: 'Deepening Connections',
            description:
              'We capture the nuance and empathy of human experts so every interaction is authentic and supportive.',
          },
          {
            title: 'Growth Trajectory',
            description:
              'AI integration into everyday life is accelerating, moving from simple tools to proactive, life-enriching companions.',
          },
        ],
        benefitsTitle: 'Key Benefits of This Evolution',
        benefits: [
          {
            title: 'Personalized Growth',
            description:
              '24/7 access to a personalized Lifestyle Coach for habit-building and personal development.',
          },
          {
            title: 'Non-Judgmental Support',
            description:
              'A safe, digital environment to practice social skills or learn languages without fear of judgment.',
          },
          {
            title: 'Democratized Expertise',
            description:
              'High-level coaching and therapeutic insights available to everyone, everywhere.',
          },
        ],
      },
    },
    cta: {
      title: 'Life is too important to improvise.',
      subtitle: 'With Digital Sidekick, you don’t have to.',
      description: 'Practice. Learn. Discuss. Cook. Date with confidence. Master your path.',
      highlight: 'Everyone deserves a sidekick.',
      button: 'Get Started',
    },
    footer: {
      terms: 'I have read and agree to the Terms of Service.',
      adult: 'Service for adults. To cancel subscription send SMS (COST 0 PLN) STOP KURS to 80711 (PLUS), STOP LIFE14 (Orange) 80713 (T-MOBILE).',
      organizer: 'Organizer: Aminta B.V. Stationsplein 28, 1382 AD Weesp. Complaints: tel (9-17) 22 244 40 22 or reklamacje@teleaudio.pl.',
    },
  },
  pl: {
    header: {
      brand: 'Digital Sidekick',
      tagline: 'Eksperckie Wskazówki. Prawdziwe Rozmowy.',
      cta: 'Otwórz Aplikację',
    },
    hero: {
      badge: 'app.digital-sidekicks.com',
      eyebrow: 'Portal AI Sidekick',
      title: 'Poznaj Swojego Cyfrowego Pomocnika',
      subtitle: 'Eksperckie wskazówki. Prawdziwe rozmowy.',
      description:
        'Platforma AI, która daje Ci natychmiastowy dostęp do wyspecjalizowanych ekspertów poprzez naturalne, ludzkie rozmowy. Nie generyczna AI. Nie pasywne narzędzia. Prawdziwe przygotowanie do prawdziwego życia.',
      note: 'Dostępny 24/7, prywatny z założenia, hiperspersonalizowany i radykalnie przystępny cenowo — jak osobista rada doradcza w kieszeni.',
      cta: 'Zacznij Teraz',
      secondaryCta: 'Zobacz Jak To Działa',
    },
    problem: {
      label: 'Problem',
      title: 'Życie pełne jest momentów, w których nie możesz sobie pozwolić na błąd',
      situations: [
        'Rozmowy kwalifikacyjne',
        'Ważne randki',
        'Decyzje zawodowe',
        'Trudne rozmowy',
        'Osobiste trudności',
        'Sytuacje społeczne',
      ],
      description:
        'Pomoc jest często droga, trudno dostępna i umawiana z tygodniowym wyprzedzeniem. W efekcie ludzie improwizują — co prowadzi do lęku, utraconych szans i żalu.',
    },
    solution: {
      label: 'Rozwiązanie',
      title: 'Poznaj Swojego Cyfrowego Pomocnika',
      subtitle:
        'Platforma AI, która daje użytkownikom natychmiastowy dostęp do wyspecjalizowanych ekspertów poprzez naturalne, ludzkie rozmowy.',
      highlight: 'Nie generyczna AI. Nie pasywne narzędzia. Prawdziwe przygotowanie do prawdziwego życia.',
      description:
        'Dostępny 24/7, prywatny z założenia, hiperspersonalizowany i radykalnie przystępny cenowo — jak osobista rada doradcza w kieszeni.',
    },
    team: {
      title: 'Twój Osobisty Zespół AI',
      subtitle: 'Specjalista na każdy ważny moment',
      members: [
        {
          icon: '🎯',
          title: 'Trener Rozmów Kwalifikacyjnych',
          description: 'Ćwicz, aż pewność siebie zastąpi nerwy.',
        },
        {
          icon: '💬',
          title: 'Twój Przyjaciel AI',
          description: 'Rozmawiaj o wszystkim bez oceniania — zawsze dostępny, gdy go potrzebujesz.',
        },
        {
          icon: '🧘',
          title: 'Towarzysz Dobrostanu',
          description: 'Bezpieczne miejsce do refleksji i wzmacniania odporności psychicznej.',
        },
        {
          icon: '♟️',
          title: 'Strateg Życiowy',
          description: 'Odzyskaj kontrolę, nawiguj przez złożoność i zaprojektuj przyszłość, jakiej pragniesz.',
        },
        {
          icon: '🗣️',
          title: 'Korepetytor Językowy',
          description: 'Mów naturalnie, wykonuj zadania razem i rozumiej głębszą logikę języka.',
        },
        {
          icon: '👨‍🍳',
          title: 'Kucharz AI',
          description: 'Gotuj mądrzej, używając składników, które już masz.',
        },
        {
          icon: '🛋️',
          title: 'Projektant Wnętrz',
          description: 'Stwórz profesjonalnie urządzony dom bez profesjonalnych cen.',
        },
        {
          icon: '✨',
          title: 'Coach Lifestyle’u',
          description: 'Zamień onieśmielające momenty w pewne siebie — od rozmów kwalifikacyjnych po pierwsze randki.',
        },
      ],
    },
    lifestyle: {
      label: 'Coach Lifestyle’u',
      title: 'Pewność Siebie Można Trenować',
      description:
        'Rozmowy kwalifikacyjne, ważne zadania w pracy, prezentacje biznesowe i pierwsze randki mogą być onieśmielające. Na szczęście pewność siebie można trenować. Ćwicz rozmowy z ‘randką swojego życia’ w całkowicie bezpiecznym środowisku.',
      points: [
        'Naucz się, co mówić — i czego nie mówić',
        'Popraw flirtowanie i komunikację',
        'Zdobądź mądre porady',
        'Przygotuj się do rozmów kwalifikacyjnych, prezentacji biznesowych i pierwszych randek',
        'Radź sobie z niezręcznymi momentami',
        'Zamień niezręczność w rozwiązania',
      ],
    },
    day: {
      title: 'Od Produktu do Codziennego Towarzysza',
      subtitle: 'Digital Sidekick staje się częścią życia ludzi',
      steps: [
        {
          icon: '🌅',
          time: 'Rano',
          title: 'Zaplanuj swój dzień ze strategiem',
          description: 'Ustal priorytety i zacznij dzień z jasnym planem.',
        },
        {
          icon: '📚',
          time: 'Popołudnie',
          title: 'Ćwicz język',
          description: 'Mów naturalnie i buduj umiejętności w prawdziwych rozmowach.',
        },
        {
          icon: '🍳',
          time: 'Wieczór',
          title: 'Gotuj z kucharzem AI',
          description: 'Zamień to, co masz w lodówce, w świetny posiłek.',
        },
        {
          icon: '💘',
          time: 'Przed randką',
          title: 'Ćwicz z Coachem Lifestyle’u',
          description: 'Wejdź pewny siebie i gotowy na rozmowę.',
        },
      ],
    },
    benefits: {
      title: 'Główne Korzyści dla Użytkownika',
      subtitle: 'Digital Sidekick staje się częścią życia ludzi',
      items: [
        {
          icon: '🚀',
          title: 'Pewność Siebie na Żądanie',
          description: 'Przygotowanie usuwa strach.',
        },
        {
          icon: '⏰',
          title: 'Zawsze Dostępny',
          description: 'Bez umawiania i bez tarcia.',
        },
        {
          icon: '🎛️',
          title: 'Przejmij Kontrolę nad Swoim Życiem',
          description: 'Przestań reagować i zacznij projektować.',
        },
        {
          icon: '💰',
          title: 'Ekspercka Pomoc Bez Eksperckich Cen',
          description: 'Oszczędzaj tysiące rocznie.',
        },
        {
          icon: '🔒',
          title: 'Prywatna Przestrzeń do Doskonalenia się',
          description: 'Ucz się bezpiecznie i szybciej.',
        },
      ],
      summary:
        'Digital Sidekick zapewnia postęp bez strachu. Użytkownicy czują się bardziej pewni siebie, mniej samotni, lepiej przygotowani i bardziej kompetentni — od codziennych kolacji po przełomowe randki i momenty definiujące karierę.',
    },
    why: {
      wins: {
        label: 'Dlaczego To Wygrywa',
        title: 'Platforma Osobistej Transformacji',
        lead: 'Inni dostarczają odpowiedzi — my budujemy lepszych ludzi.',
        description:
          'Nasze zalety obejmują doświadczenia oparte na personie, inteligencję emocjonalną, praktykę opartą na scenariuszach, przypadki użycia tworzące nawyki i bardzo wysoką trafność życiową.',
        highlight: 'Definiujemy kategorię Osobistych Towarzyszy AI.',
      },
      now: {
        label: 'Dlaczego Teraz',
        title: 'Moment Nigdy Nie Był Lepszy',
        description:
          'AI jest zaufana, samodoskonalenie kwitnie, a konsumenci budują relacje z technologią. Kolejne dominujące platformy będą prowadzić użytkowników — nie tylko ich informować.',
        highlight:
          'Pytanie brzmi już nie czy ludzie będą polegać na AI, ale której AI powierzą swoje życie.',
      },
    },
    business: {
      model: {
        label: 'Model Biznesowy',
        title: 'Subskrypcja i Ekspansja',
        cards: [
          {
            title: 'Subskrypcja',
            description:
              'Premium obejmuje nieograniczony dostęp, zaawansowanych ekspertów i angażujące persony, takie jak Coach Lifestyle’u, strateg i korepetytor.',
          },
          {
            title: 'Ekspansja',
            description:
              'Przyszła ekspansja obejmuje pakiety mistrzostwa w relacjach, pakiety przyspieszenia kariery, elitarne poziomy stratega i coaching pewności siebie.',
          },
        ],
        note: 'Zbudowane dla silnej retencji i dożywotniej wartości.',
      },
      vision: {
        label: 'Wizja',
        title: 'Pomocnik dla Każdego',
        description:
          'Wyobraź sobie świat, w którym każdy ma coacha, stratega, tutora, kucharza, projektanta, doradcę wellness i randkowego — wszystkich w kieszeni.',
        highlight: 'Nie elitarne wsparcie — standardowa ludzka infrastruktura.',
        note: 'Naszą ambicją jest stanie się domyślnym towarzyszem AI na co dzień.',
      },
    },
    service: {
      program: {
        label: 'Program Serwisowy',
        title: 'Gdzie Ludzka Ekspertyza Spotyka Sztuczną Inteligencję',
        items: [
          {
            title: 'Integracja Ekspertów',
            description:
              'Wychwytujemy niuanse i empatię ludzkich ekspertów, aby każda interakcja była autentyczna i wspierająca.',
          },
          {
            title: 'Synergia Techniczna',
            description:
              'Ta wiedza ekspercka jest płynnie zintegrowana z zaawansowanymi możliwościami AI naszego dedykowanego zespołu technicznego.',
          },
          {
            title: 'Zapewnienie Jakości',
            description:
              'Każda persona przechodzi rygorystyczny rozwój, aby zapewnić, że udzielane wskazówki są dokładne, etyczne i zorientowane na wzrost.',
          },
        ],
      },
      future: {
        label: 'Przyszłość Cyfrowych Osobowości',
        title: 'Kształtowanie Przyszłości Interakcji Człowiek-AI',
        cards: [
          {
            title: 'Pogłębianie Połączeń',
            description:
              'Wychwytujemy niuanse i empatię ludzkich ekspertów, aby każda interakcja była autentyczna i wspierająca.',
          },
          {
            title: 'Trajektoria Wzrostu',
            description:
              'Integracja AI w codzienne życie przyspiesza, przechodząc od prostych narzędzi do proaktywnych towarzyszy wzbogacających życie.',
          },
        ],
        benefitsTitle: 'Kluczowe Korzyści Tej Ewolucji',
        benefits: [
          {
            title: 'Spersonalizowany Wzrost',
            description:
              'Dostęp 24/7 do spersonalizowanego Coacha Lifestyle’u do budowania nawyków i rozwoju osobistego.',
          },
          {
            title: 'Wsparcie Bez Oceniania',
            description:
              'Bezpieczne, cyfrowe środowisko do ćwiczenia umiejętności społecznych lub nauki języków bez strachu przed oceną.',
          },
          {
            title: 'Zdemokratyzowana Ekspertyza',
            description:
              'Coaching na wysokim poziomie i spostrzeżenia terapeutyczne dostępne dla każdego, wszędzie.',
          },
        ],
      },
    },
    cta: {
      title: 'Życie jest zbyt ważne, aby improwizować.',
      subtitle: 'Z Digital Sidekick nie musisz tego robić.',
      description: 'Ćwicz. Ucz się. Przedyskutuj. Gotuj. Randkuj z pewnością siebie. Zapanuj nad swoją ścieżką.',
      highlight: 'Każdy zasługuje na pomocnika.',
      button: 'Zacznij Teraz',
    },
    footer: {
      terms: 'Zapoznałem się z treścią regulaminu Usługi Dostępowej.',
      adult: 'Serwis dla osób pełnoletnich, aby zrezygnować z subskrypcji wyślij SMS ( KOSZT 0 zł ) STOP KURS na 80711 ( PLUS) , STOP LIFE14 (Orange ) 80713 ( T-MOBILE ).',
      organizer: 'Organizator: Aminta B.V. Stationsplein 28 , 1382 AD Weesp. Reklamacje: tel (9-17) 22 244 40 22 lub reklamacje@teleaudio.pl.',
    },
  },
};
